import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { Company } from "@productlane/zero"
import { queries } from "@productlane/zero/queries"

import { useCompaniesFilterState } from "../filter/companies-filter-state"
import {
	resolveCompaniesSort,
	useCompaniesSortState,
} from "./companies-sort-state"

const flip = (order: "asc" | "desc"): "asc" | "desc" =>
	order === "asc" ? "desc" : "asc"

/**
 * The companies either side of this one in the customer list, with the list's
 * own sort and filters, read one row each way instead of loading the list.
 */
export function useAdjacentCompanies(company: Company | null | undefined) {
	const { sortType } = useCompaniesSortState()
	const { tiers, statuses, ownerIds, revenue, size, sources, negations } =
		useCompaniesFilterState()
	const { sortColumn, sortOrder } = resolveCompaniesSort(sortType)

	const filters = useMemo(() => {
		if (
			tiers.length === 0 &&
			statuses.length === 0 &&
			ownerIds.length === 0 &&
			revenue === null &&
			size === null &&
			sources.length === 0
		)
			return undefined
		return {
			negations,
			tiers: tiers.length > 0 ? tiers : undefined,
			statuses: statuses.length > 0 ? statuses : undefined,
			ownerIds: ownerIds.length > 0 ? ownerIds : undefined,
			revenue: revenue ?? undefined,
			size: size ?? undefined,
			sources: sources.length > 0 ? sources : undefined,
		}
	}, [tiers, statuses, ownerIds, revenue, size, sources, negations])

	const cursor = company
		? {
				name: company.name,
				createdAt: company.createdAt,
				revenue: company.revenue,
				size: company.size,
				tierName: company.tierName,
				statusName: company.statusName,
				id: company.id,
			}
		: undefined

	const [next] = useQuery(
		queries.companiesWithContacts({
			filters,
			sortColumn,
			sortOrder,
			limit: 1,
			startAfter: cursor,
		}),
		{ enabled: !!company },
	)
	const [previous] = useQuery(
		queries.companiesWithContacts({
			filters,
			sortColumn,
			sortOrder: flip(sortOrder),
			limit: 1,
			startAfter: cursor,
		}),
		{ enabled: !!company },
	)

	return { nextId: next[0]?.id ?? null, previousId: previous[0]?.id ?? null }
}
